import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useOutletContext } from "react-router-dom";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import {
  createPlatformWorkspace,
  getBillingPlanCatalog,
  getPlatformWorkspacePresentationStatus,
} from "../../../api/client";
import WorkspaceCreateWizard from "../../../components/workspaces/WorkspaceCreateWizard";
import { PLATFORM_CONSOLE_PATH, workspaceDetailPath } from "./platformConsoleRoutes";

function Section({ children }) {
  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        bgcolor: alpha(theme.palette.secondary.main, 0.04),
        borderColor: "divider",
        p: 3,
      })}
    >
      {children}
    </Paper>
  );
}

const PlatformWorkspaceCreatePage = () => {
  const { t } = useTranslation("app");
  const navigate = useNavigate();
  const outlet = useOutletContext() || {};
  const [planItems, setPlanItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getBillingPlanCatalog()
      .then((res) => {
        if (!cancelled) setPlanItems(Array.isArray(res?.items) ? res.items : []);
      })
      .catch(() => {
        if (!cancelled) setPlanItems([]);
      });
    if (typeof outlet.stopRefreshing === "function") outlet.stopRefreshing();
    return () => {
      cancelled = true;
    };
  }, [outlet]);

  const handleCompleted = useCallback(
    (result) => {
      if (result?.dominioId) {
        navigate(workspaceDetailPath(result.dominioId));
      }
    },
    [navigate]
  );

  return (
    <Section>
      <Stack spacing={2.5} sx={{ minWidth: 0 }}>
        <Button
          component={RouterLink}
          to={PLATFORM_CONSOLE_PATH.workspaces}
          startIcon={<ArrowBackIcon />}
          size="small"
          variant="outlined"
          sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 600 }}
        >
          {t("workspaceCreate.page.backToList")}
        </Button>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" component="h2" sx={{ mb: 1, fontWeight: 700, letterSpacing: -0.02 }}>
            {t("workspaceCreate.page.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720, lineHeight: 1.55 }}>
            {t("workspaceCreate.page.subtitle")}
          </Typography>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <WorkspaceCreateWizard
            createWorkspace={createPlatformWorkspace}
            getPresentationStatus={getPlatformWorkspacePresentationStatus}
            planCatalogItems={planItems}
            onCompleted={handleCompleted}
            openWorkspaceUrl={(res) => (res?.dominioId ? workspaceDetailPath(res.dominioId) : null)}
          />
        </Box>

        <Button
          component={RouterLink}
          to={PLATFORM_CONSOLE_PATH.workspaces}
          variant="text"
          color="primary"
          sx={{ alignSelf: "flex-end", textTransform: "none", fontWeight: 600 }}
        >
          {t("common.cancel")}
        </Button>
      </Stack>
    </Section>
  );
};

export default PlatformWorkspaceCreatePage;
